import { FC, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Slide from '@mui/material/Slide'
import Chip from '@mui/material/Chip'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { themeColor } from '../../layout/theme/color'
import { WORKFLOW } from './tokens'
import { Node } from '@xyflow/react'
import {
  WorkflowNodeData,
  WorkflowNodeType,
} from './types'
import InputDataFlyout from './components/InputDataFlyout'
import TriggerConfig from './config/TriggerConfig'
import ToolCallConfig from './config/ToolCallConfig'
import AiCallConfig from './config/AiCallConfig'
import ConditionConfig from './config/ConditionConfig'
import LoopConfig from './config/LoopConfig'
import TransformConfig from './config/TransformConfig'
import FieldMappingConfig from './config/FieldMappingConfig'
import MergeConfig from './config/MergeConfig'
import VisualizationConfig from './config/VisualizationConfig'
import DataConfig from './config/DataConfig'
import ParseConfig from './config/ParseConfig'
import DataStoreWriteConfig from './config/DataStoreWriteConfig'
import DataStoreReadConfig from './config/DataStoreReadConfig'
import DataStoreUpdateConfig from './config/DataStoreUpdateConfig'
import DataStoreDeleteConfig from './config/DataStoreDeleteConfig'
import DataStoreConfig from './config/DataStoreConfig'

interface NodeConfigPanelProps {
  node: Node | null
  onUpdate: (data: Partial<WorkflowNodeData>) => void
  onRenameNode?: (oldId: string, newId: string) => void
  onDelete: () => void
  onClose: () => void
  workflowId?: string
  testConfig?: Record<string, unknown>
}

const NODE_TYPE_LABELS: Record<WorkflowNodeType, string> = {
  trigger: 'Trigger',
  // eslint-disable-next-line camelcase
  tool_call: 'MCP Tool Call',
  // eslint-disable-next-line camelcase
  ai_call: 'AI Call',
  condition: 'Condition',
  loop: 'Loop',
  transform: 'Transform',
  // eslint-disable-next-line camelcase
  field_mapping: 'Field mapping',
  merge: 'Merge',
  visualization: 'Visualization',
  data: 'Data',
  parse: 'Parse',
  // eslint-disable-next-line camelcase
  data_store_write: 'Data store: write',
  // eslint-disable-next-line camelcase
  data_store_read: 'Data store: read',
  // eslint-disable-next-line camelcase
  data_store_update: 'Data store: update',
  // eslint-disable-next-line camelcase
  data_store_delete: 'Data store: delete',
  // eslint-disable-next-line camelcase
  data_store: 'Data store',
}

const NodeConfigPanel: FC<NodeConfigPanelProps> = ({ node, onUpdate, onRenameNode, onDelete, onClose, workflowId, testConfig }) => {
  const translate = useTranslate()
  const isOpen = node !== null
  const [editingId, setEditingId] = useState<string>('')
  // Lives here so the flyout keeps its open state when switching nodes.
  const [inputDataOpen, setInputDataOpen] = useState(false)

  const renderConfig = () => {
    if (!node) return null
    const data = node.data as WorkflowNodeData

    const nodeData = data as unknown as WorkflowNodeData
    switch (nodeData.type) {
      case 'trigger':
        return <TriggerConfig data={nodeData} onUpdate={onUpdate} />
      case 'tool_call':
        return <ToolCallConfig data={nodeData} onUpdate={onUpdate} nodeId={node.id} workflowId={workflowId} testConfig={testConfig} />
      case 'ai_call':
        return <AiCallConfig data={nodeData} onUpdate={onUpdate} />
      case 'condition':
        return <ConditionConfig data={nodeData} onUpdate={onUpdate} />
      case 'loop':
        return <LoopConfig data={nodeData} onUpdate={onUpdate} />
      case 'transform':
        return <TransformConfig data={nodeData} onUpdate={onUpdate} />
      case 'field_mapping':
        return <FieldMappingConfig data={nodeData} onUpdate={onUpdate} />
      case 'merge':
        return <MergeConfig data={nodeData} onUpdate={onUpdate} />
      case 'visualization':
        return <VisualizationConfig data={nodeData} onUpdate={onUpdate} />
      case 'data':
        return <DataConfig data={nodeData} onUpdate={onUpdate} />
      case 'parse':
        return <ParseConfig data={nodeData} onUpdate={onUpdate} />
      case 'data_store_write':
        return <DataStoreWriteConfig data={nodeData} onUpdate={onUpdate} />
      case 'data_store_read':
        return <DataStoreReadConfig data={nodeData} onUpdate={onUpdate} />
      case 'data_store_update':
        return <DataStoreUpdateConfig data={nodeData} onUpdate={onUpdate} />
      case 'data_store_delete':
        return <DataStoreDeleteConfig data={nodeData} onUpdate={onUpdate} />
      case 'data_store':
        return <DataStoreConfig data={nodeData} onUpdate={onUpdate} />
      default:
        return null
    }
  }

  const nodeData = node?.data as unknown as WorkflowNodeData | undefined
  const nodeType = nodeData?.type
  // eslint-disable-next-line security/detect-object-injection
  const colors = nodeType ? WORKFLOW[nodeType] : undefined
  // eslint-disable-next-line security/detect-object-injection
  const nodeTypeLabel = nodeType ? NODE_TYPE_LABELS[nodeType] : ''

  return (
    <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
      <Box
        sx={{
          width: WORKFLOW.configPanel.width,
          borderLeft: `1px solid ${themeColor.slate10}`,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          // Visible + relative so the input-data flyout can hang off the left edge.
          overflow: 'visible',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {/* Triggers have no upstream input by definition — no flyout there. */}
        {node && nodeType !== 'trigger' && (
          <InputDataFlyout
            open={inputDataOpen}
            onToggle={() => setInputDataOpen((v) => !v)}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${themeColor.slate10}`,
          }}
        >
          {nodeType && colors && (
            <Chip
              label={nodeTypeLabel}
              size="small"
              sx={{
                height: 24,
                fontSize: 11,
                fontWeight: 600,
                background: colors.bg,
                color: colors.accent,
                borderRadius: '6px',
              }}
            />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            {translate('workflow.config.title')}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {node && onRenameNode && (
            <TextField
              label="Node ID"
              size="small"
              fullWidth
              value={editingId || node.id}
              onChange={(e) => setEditingId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              onFocus={() => setEditingId(node.id)}
              onBlur={() => {
                if (editingId && editingId !== node.id) {
                  onRenameNode(node.id, editingId)
                }
                setEditingId('')
              }}
              sx={{ mb: 2 }}
              InputProps={{
                sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 },
              }}
            />
          )}
          {/* Keyed by node id so config panels (and their local field state,
              e.g. the fixed/expression mode per parameter) remount when
              another node is selected instead of reusing stale state. */}
          {node && <Box key={node.id}>{renderConfig()}</Box>}
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${themeColor.slate10}` }}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={onDelete}
            sx={{ textTransform: 'none' }}
          >
            {translate('workflow.config.deleteNode')}
          </Button>
        </Box>
      </Box>
    </Slide>
  )
}

export default NodeConfigPanel
